import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import {
  getShellMenuItemSx,
  getShellMenuPaperSx,
  getShellMenuUserHeaderSx,
} from '../../utils/mobileShellMenuLayout';

const REPORT_ICONS = {
  stock_summary: AssessmentOutlinedIcon,
  current_stock: Inventory2OutlinedIcon,
  stock_movement: SwapHorizOutlinedIcon,
  consumption: RestaurantOutlinedIcon,
  adjustment: TuneOutlinedIcon,
  transfer: CompareArrowsOutlinedIcon,
  low_stock: WarningAmberOutlinedIcon,
  valuation: CurrencyRupeeOutlinedIcon,
};

const menuFocusProps = {
  autoFocus: false,
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
  MenuListProps: { autoFocusItem: false },
};

const StockReportTypeMenu = ({ isDark, reports = [], value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selected = reports.find((report) => report.id === value) || reports[0];
  const SelectedIcon = REPORT_ICONS[selected?.id] || BarChartOutlinedIcon;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (reportId) => {
    handleClose();
    if (reportId !== value) {
      onChange(reportId);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen(event);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.5,
          minHeight: 40,
          borderRadius: '12px',
          cursor: 'pointer',
          userSelect: 'none',
          color: isDark ? '#ddd' : 'rgb(88, 91, 105)',
          backgroundColor: open
            ? isDark
              ? 'rgba(76, 225, 182, 0.15)'
              : 'rgba(76, 225, 182, 0.12)'
            : 'transparent',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: open
            ? isDark
              ? '0 2px 8px rgba(76, 225, 182, 0.2)'
              : '0 2px 8px rgba(76, 225, 182, 0.15)'
            : 'none',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
            transform: 'translateY(-1px)',
            boxShadow: isDark
              ? '0 4px 12px rgba(76, 225, 182, 0.25)'
              : '0 4px 12px rgba(76, 225, 182, 0.2)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: '#4ce1b6',
            color: '#04120e',
            fontSize: '14px',
            fontWeight: 700,
            boxShadow: '0 2px 4px rgba(76, 225, 182, 0.3)',
          }}
        >
          <SelectedIcon sx={{ fontSize: 16 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: isDark ? '#f8fafc' : '#212121',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            {selected?.label || 'Select report'}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              color: isDark ? 'rgba(148,163,184,0.95)' : '#646777',
              lineHeight: 1.2,
            }}
          >
            Report type
          </Typography>
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 20,
            color: isDark ? '#94a3b8' : '#646777',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        {...menuFocusProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 1300 }}
        PaperProps={{
          sx: {
            ...getShellMenuPaperSx(isDark),
            width: 280,
            borderRadius: '12px',
            padding: '8px 0',
            marginTop: '8px',
          },
        }}
      >
        <Box sx={getShellMenuUserHeaderSx(isDark)}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#4ce1b6',
              color: '#04120e',
              fontWeight: 700,
              boxShadow: '0 2px 4px rgba(76, 225, 182, 0.3)',
            }}
          >
            <BarChartOutlinedIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: isDark ? '#f8fafc' : '#212121',
                lineHeight: 1.25,
              }}
            >
              Inventory Reports
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: isDark ? 'rgba(148,163,184,0.95)' : '#646777',
                lineHeight: 1.3,
              }}
            >
              Choose a report to view
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgb(223, 227, 235)' }} />

        {reports.map((report) => {
          const Icon = REPORT_ICONS[report.id] || BarChartOutlinedIcon;
          const isSelected = report.id === value;

          return (
            <MenuItem
              key={report.id}
              onClick={() => handleSelect(report.id)}
              selected={isSelected}
              sx={{
                ...getShellMenuItemSx(isDark),
                height: 40,
                px: 2,
                ...(isSelected
                  ? {
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                      '& .MuiListItemIcon-root': { color: '#4ce1b6' },
                      '& .MuiListItemText-primary': {
                        color: isDark ? '#4ce1b6' : '#0d9488',
                        fontWeight: 700,
                      },
                    }
                  : {}),
              }}
            >
              <ListItemIcon>
                <Icon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <ListItemText
                primary={report.label}
                primaryTypographyProps={{ fontSize: '14px', fontWeight: isSelected ? 700 : 600 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default StockReportTypeMenu;
